import React, { useEffect, useState } from "react";
import { getConsent } from "@/components/CookieBanner";

/**
 * - Sends Google Consent Mode v2 update to the globally loaded gtag (Google Ads AW-18463610714 in index.html).
 * - Grants/revokes consent for the Meta Pixel (2561156244355923, loaded in index.html).
 * - Optionally loads Google Analytics 4 when REACT_APP_GA_ID=G-XXXXXXXXXX is set.
 */
const GA_ID = process.env.REACT_APP_GA_ID;

// Meta Pixel is loaded in index.html with consent revoked; grant it once marketing cookies are accepted.
const updateMetaConsent = (consent) => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  window.fbq("consent", consent.marketing ? "grant" : "revoke");
};

/**
 * Push Google Consent Mode v2 update based on user's cookie banner choice.
 * Runs whether or not GA_ID is configured — Google Ads (loaded in index.html) reads this.
 */
const updateGoogleConsent = (consent) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const analyticsGranted = consent.analytics ? "granted" : "denied";
  const marketingGranted = consent.marketing ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage: marketingGranted,
    ad_user_data: marketingGranted,
    ad_personalization: marketingGranted,
    analytics_storage: analyticsGranted,
  });
};

const injectScript = (id, code) => {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  s.text = code;
  document.head.appendChild(s);
};

const injectSrc = (id, src) => {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
};

const loadGA = () => {
  if (!GA_ID) return;
  injectSrc("ga-src", `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
  injectScript(
    "ga-init",
    `window.dataLayer = window.dataLayer || [];
     function gtag(){dataLayer.push(arguments);}
     window.gtag = gtag;
     gtag('js', new Date());
     gtag('config', '${GA_ID}', { anonymize_ip: true });`
  );
};

// Google Ads conversion labels (AW-18463610714). Replace the placeholders with the labels from Google Ads.
const GADS_ID = "AW-18463610714";
export const GADS_LABEL_WHATSAPP = "LABEL_WHATSAPP";
export const GADS_LABEL_APPEL = "LABEL_APPEL";

export const fireAdsConversion = (label) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }
  window.gtag("event", "conversion", { send_to: `${GADS_ID}/${label}` });
};

// Helper to fire conversion events from anywhere (e.g. after quote form submit).
// Google Ads conversions fire via gtag as long as consent was granted (Consent Mode v2).
export const trackConversion = (eventName = "generate_lead", params = {}) => {
  const c = getConsent();
  if (c && typeof window !== "undefined" && typeof window.gtag === "function") {
    // Fires to any configured gtag destination (Google Ads AW-... + optional GA4)
    window.gtag("event", eventName, params);
  }
  // Meta Pixel: fbq queues the event while consent is revoked and sends it once granted.
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    const metaName = eventName === "generate_lead" ? "Lead" : eventName;
    window.fbq("track", metaName, params);
  }
};

const Analytics = () => {
  const [consent, setConsent] = useState(() => getConsent());

  useEffect(() => {
    const handler = (e) => setConsent(e.detail);
    window.addEventListener("vw:consent-updated", handler);
    return () => window.removeEventListener("vw:consent-updated", handler);
  }, []);

  useEffect(() => {
    if (!consent) return;
    updateGoogleConsent(consent);
    updateMetaConsent(consent);
    if (consent.analytics) loadGA();
  }, [consent]);

  // Site-wide click tracking: every wa.me link → WhatsApp conversion, every tel: link → Appel conversion.
  useEffect(() => {
    const onClick = (e) => {
      const link = e.target.closest && e.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      if (/wa\.me|api\.whatsapp\.com|whatsapp:\/\//i.test(href)) {
        fireAdsConversion(GADS_LABEL_WHATSAPP);
      } else if (/^tel:/i.test(href)) {
        fireAdsConversion(GADS_LABEL_APPEL);
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
};

export default Analytics;
